import { useForm } from '@mantine/form'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import type {
  Artwork,
  CreateArtworkFromTaskInput,
  Task,
} from 'src/services/apollo/generated/graphql'
import { ArtworkVisibilityType } from 'src/services/apollo/generated/graphql'
import {
  executeCreateArtworkFromTaskV2,
  refetchGetTaskById,
} from 'src/services/apollo/task'
import { getTaskMediaIdByIndex } from 'src/utils/task'
import { track, trackUserPreference, useTrackPageView } from 'src/utils/tracker'
import useLoadingCallback from 'src/utils/useLoadingCallback'
import { extractTackIds } from '~/services/helpers/tack'
import type {
  ArtworkInfoFormProps,
  ArtworkInfoFormValue,
} from '../SubmitArtwork/ArtworkInfoForm'
import {
  ArtworkInfoForm,
  mapTaskToArtworkInfoFormValue,
  mergeArtworkInfoFormFields,
} from '../SubmitArtwork/ArtworkInfoForm'
import type { UIButtonProps } from '../ui/Button'
import { UIButton } from '../ui/Button'
import { UIForm } from '../ui/Form'
import {
  UIDialogContent,
  UIDialogFooter,
  UIDialogHeader,
  UIDialogResponsive,
} from '../ui/Modal'
import { isMediaFaved } from './MultiColumnGenerationForm/utils'

const getTaskType = (task: Task) => {
  const { batchSize = 1, upscale = 1, enlarge = 1 } = task.parameters
  if (batchSize === 3) return 'variation'
  if (enlarge > 1) return 'upscale'
  if (upscale > 1) return 'enhance'
  return 'generation'
}

export type PublishTaskButtonProps = {
  task?: Task
  scene?: string
  formProps?: Omit<PublishTaskFormProps, 'task' | 'scene'>
} & UIButtonProps

export const PublishTaskButton = ({
  task,
  scene,
  formProps = {},
  ...rest
}: PublishTaskButtonProps) => {
  const { t } = useTranslation(['artwork'])
  const [isPublishTaskDialogOpen, setIsTaskSubmitDialogOpen] = useState(false)

  const { onSuccess, ...restOfForm } = formProps
  const handleOnSuccess = () => {
    setIsTaskSubmitDialogOpen(false)
    onSuccess?.()
  }

  return (
    <>
      <UIButton
        variant="filled"
        onPress={() => {
          track('publish_btn_click', {
            scene: 'tasks',
          })
          setIsTaskSubmitDialogOpen(true)
        }}
        {...rest}
      >
        {t('artwork:publish')}
      </UIButton>

      <UIDialogResponsive
        open={isPublishTaskDialogOpen}
        onClose={() => setIsTaskSubmitDialogOpen(false)}
        dismissable
        className="mlg:w-[600px] max-w-full"
      >
        {task && (
          <PublishTaskForm
            task={task}
            scene={scene}
            onSuccess={handleOnSuccess}
            {...restOfForm}
          />
        )}
      </UIDialogResponsive>
    </>
  )
}

export type PublishTaskFormProps = {
  task: Task
  scene?: string
  section?: string
} & {
  onSuccess?: () => void
  afterPublish?: (artwork: Artwork) => void
  mediaIdx?: number
  initialValues?: Partial<ArtworkInfoFormValue>
  formProps?: Omit<ArtworkInfoFormProps<ArtworkInfoFormValue>, 'form'>
}

export type PublishTaskFormValue = ArtworkInfoFormValue

export const PublishTaskForm = ({
  task,
  scene,
  section,
  onSuccess,
  afterPublish,
  mediaIdx,
  initialValues,
  formProps: { fields, ...restOfForm } = {},
}: PublishTaskFormProps) => {
  const { t } = useTranslation(['artwork'])
  // Tracking
  const trackPageView = useTrackPageView()
  const dialogOpenAt = useRef(new Date().getTime())
  useEffect(() => {
    trackPageView({ scene: 'publish' })
    // Only track once when the component mounts
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Form Handling
  const form = useForm<PublishTaskFormValue>({
    initialValues: mapTaskToArtworkInfoFormValue(task, initialValues),
  })
  const [loading, submit] = useLoadingCallback(
    async (values: PublishTaskFormValue) => {
      try {
        const input = {
          description: values.description,
          extra: {
            ...values.extra,
            ...(values.challenge && { challenge: values.challenge }),
          },
          hidePrompts: values.hidePrompts,
          isPrivate: values.visibility === ArtworkVisibilityType.Private,
          visibility: values.visibility,
          mediaIndex: mediaIdx,
          tags: [],
          tackIds: extractTackIds(values.tags),
          title: values.title,
        } satisfies CreateArtworkFromTaskInput

        const resp = await executeCreateArtworkFromTaskV2({
          taskId: task.id,
          input: input,
        })

        track('publish_success', {
          isPrivate: values.visibility === ArtworkVisibilityType.Private,
          scene,
          taskType: getTaskType(task),
          section,
          hidePrompts: values.hidePrompts,
        })

        trackUserPreference({
          type: 'publish',
          taskParams: task.parameters,
          taskId: task.id,
          publishParams: {
            ...input,
            extra: {
              ...input.extra,
              description: input.description,
            },
            hasFav:
              !!task.favoritedAt ||
              isMediaFaved(task, resp.data?.createArtworkFromTaskV2?.mediaId),
          },
          action: 'confirm',
          otherParams: {
            scene,
            mediaIdx,
            mediaId: resp.data?.createArtworkFromTaskV2?.mediaId,
            duration: new Date().getTime() - dialogOpenAt.current,
          },
        })
        toast.success(t('artwork:notice.publish-successfully'))
        refetchGetTaskById(task.id)
        onSuccess?.()

        if (resp.data?.createArtworkFromTaskV2) {
          afterPublish?.(resp.data.createArtworkFromTaskV2)
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        const message = error.message as string | undefined
        if (message === 'Contest requires artwork from generation task') {
          toast.error(
            t(
              'artwork:artwork.notice.contest-requirement.from-generation-task',
            ),
          )
        } else if (
          message === 'Contest requires task without reference image'
        ) {
          toast.error(
            t(
              'artwork:artwork.notice.contest-requirement.without-reference-image',
            ),
          )
        } else if (message === 'Contest requires task with reference image') {
          toast.error(
            t(
              'artwork:artwork.notice.contest-requirement.with-reference-image',
            ),
          )
        } else if (message?.startsWith('Contest requires model')) {
          toast.error(
            t('artwork:artwork.notice.contest-requirement.with-specific-model'),
          )
        } else if (
          message?.startsWith('Contest requires LoRA') &&
          message.includes('weight in')
        ) {
          toast.error(
            t(
              'artwork:artwork.notice.contest-requirement.with-specific-lora-weight',
            ),
          )
        } else if (message?.startsWith('Contest requires LoRA')) {
          toast.error(
            t('artwork:artwork.notice.contest-requirement.with-specific-lora'),
          )
        } else {
          toast.error(`Failed to publish. ${error?.message}`)
        }
      }
    },
    [afterPublish],
  )

  return (
    <UIForm
      form={form}
      onSubmit={submit}
      className="flex-1 min-h-0 max-h-[calc(100dvh-32px)] flex flex-col"
    >
      <UIDialogHeader heading={t('artwork:generation.publish-settings')} />

      <UIDialogContent className="overflow-y-auto flex-1 min-h-0 flex flex-col gap-4 overscroll-none">
        <ArtworkInfoForm
          form={form}
          fields={mergeArtworkInfoFormFields(fields, {
            prompts: false,
            description: true,
          })}
          mediaId={getTaskMediaIdByIndex(task, mediaIdx)}
          {...restOfForm}
        />
      </UIDialogContent>

      <UIDialogFooter className="justify-end">
        <UIButton
          size="large"
          type="submit"
          busy={loading}
          variant="filled"
          className="w-full"
        >
          {t('artwork:publish')}
        </UIButton>
      </UIDialogFooter>
    </UIForm>
  )
}
